/*
  Shared tree view: the inverted NIP-1971 problem tree markup plus its GSAP
  transitions. Target-agnostic — it renders a `ProblemDag` and knows nothing
  about where the events came from.
*/
import { gsap } from "gsap";
import {
  childCoordinates, statusLabel, subtreeStatusCounts,
  type ProblemDag, type StatusRollup
} from "./problem-dag";
import { rocketLabel, type Rocket } from "./rockets";

export const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

export interface TreeViewState {
  dag: ProblemDag;
  collapsed: ReadonlySet<string>;
  selected: string;
  /** Rockets indexed by the problem coordinate they are solving (kind 31108 `problem` tag). */
  rocketsByProblem?: ReadonlyMap<string, Rocket[]>;
}

export const escapeHtml = (value: string) => value.replace(/[&<>"]/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;"
})[character]!);

export function rollupBadges(rollup: StatusRollup): string {
  return `
    <span class="badge badge-open${rollup.open ? "" : " zero"}" title="Open problems in this subtree">
      <b>${rollup.open}</b> open</span>
    <span class="badge badge-progress${rollup.claimed ? "" : " zero"}" title="In-progress (claimed) problems in this subtree">
      <b>${rollup.claimed}</b> in progress</span>`;
}

/**
 * Rocket chips shown under a problem card: every rocket whose kind 31108
 * `problem` tag points at this coordinate. Each chip opens the rocket view.
 */
function rocketChips(rockets: readonly Rocket[]): string {
  return `<div class="node-rockets" aria-label="Rockets solving this problem">${rockets.map((rocket) => {
    const label = rocketLabel(rocket);
    const hint = `${label}${rocket.mission ? ` — ${rocket.mission}` : ""}. Open the rocket view.`;
    return `<button class="rocket-chip" type="button" data-rocket="${escapeHtml(rocket.coordinate)}" title="${escapeHtml(hint)}">
      <span class="rocket-logo" aria-hidden="true">
        <svg class="rocket-glyph" viewBox="0 0 24 24" focusable="false">
          <path d="M12 1.5c2.4 2 3.8 5 3.8 8.6 0 1.3-.2 2.5-.6 3.6H8.8a12.6 12.6 0 0 1-.6-3.6C8.2 6.5 9.6 3.5 12 1.5z"/>
          <path d="M8.2 14.2 5.6 16.4l.9 3.3 2.4-2zM15.8 14.2l2.6 2.2-.9 3.3-2.4-2z"/>
          <path d="M12 17.6c.9 1.2.9 2.6 0 3.9-.9-1.3-.9-2.7 0-3.9z"/>
        </svg>
      </span>
      <span class="rocket-name">${escapeHtml(label)}</span>
    </button>`;
  }).join("")}</div>`;
}

function treeBranch(state: TreeViewState, coordinate: string, trail: ReadonlySet<string>): string {
  if (trail.has(coordinate)) return "";
  const node = state.dag.nodes.get(coordinate);
  if (!node) return "";
  const nextTrail = new Set(trail).add(coordinate);
  const children = childCoordinates(state.dag, coordinate).filter((child) => !nextTrail.has(child));
  const isCollapsed = state.collapsed.has(coordinate);
  const isSelected = state.selected === coordinate;
  const rockets = state.rocketsByProblem?.get(coordinate) ?? [];
  return `<li class="branch" data-coordinate="${coordinate}">
    <div class="node">
      <button class="node-card status-${node.status}${isSelected ? " selected" : ""}" data-open="${coordinate}"
        aria-current="${isSelected ? "true" : "false"}" title="Open ${escapeHtml(node.title)}">
        <span class="node-topline">
          <span class="status-chip status-chip-${node.status}">${statusLabel(node.status)}</span>
          ${node.forkCount ? `<span class="badge badge-fork" title="Forked heads">${node.forkCount + 1} heads</span>` : ""}
        </span>
        <span class="node-title">${escapeHtml(node.title)}</span>
        <span class="node-rollup">${rollupBadges(subtreeStatusCounts(state.dag, coordinate))}</span>
      </button>
      ${rockets.length ? rocketChips(rockets) : ""}
      ${children.length ? `
        <button class="toggle" data-toggle="${coordinate}" aria-expanded="${!isCollapsed}"
          title="${isCollapsed ? `Expand ${children.length} child problem${children.length === 1 ? "" : "s"}` : "Collapse branch"}">
          ${isCollapsed ? `+${children.length}` : "−"}
        </button>` : `<span class="leaf-dot" aria-hidden="true"></span>`}
    </div>
    ${children.length && !isCollapsed
      ? `<ul>${children.map((child) => treeBranch(state, child, nextTrail)).join("")}</ul>`
      : ""}
  </li>`;
}

export function treeMarkup(state: TreeViewState): string {
  const rootChildren = treeBranch(state, state.dag.rootCoordinate, new Set());
  return rootChildren
    ? `<div class="canvas-inner"><ul class="tree">${rootChildren}</ul></div>`
    : `<div class="canvas-inner"><p class="empty-tree">No child problems below this root yet.</p></div>`;
}

export interface RenderTreeOptions {
  animate?: boolean;
  /** Animate only this branch (used when a collapsed branch reopens). */
  animateCoordinate?: string;
  zoom?: number;
  /** Scroll the root card into view. Only wanted when a tree is first loaded. */
  center?: boolean;
}

/**
 * The tree is centered inside a `max-content` box, so a tree wider than the
 * frame leaves its root — the middle of that box — off screen. Bring it back.
 * Assumes the zoom transform is identity (true right after a tree loads).
 */
export function centerOnRoot(canvas: HTMLElement) {
  const rootCard = canvas.querySelector<HTMLElement>(".node-card");
  if (!rootCard) return;
  const card = rootCard.getBoundingClientRect();
  const box = canvas.getBoundingClientRect();
  canvas.scrollLeft = Math.max(0, canvas.scrollLeft + (card.left - box.left) - (box.width - card.width) / 2);
}

export function renderTree(canvas: HTMLElement, state: TreeViewState, options: RenderTreeOptions = {}) {
  const { animate = true, animateCoordinate = "", zoom = 1, center = false } = options;
  canvas.innerHTML = treeMarkup(state);
  applyZoom(canvas, zoom, false);
  if (center) centerOnRoot(canvas);
  if (!animate || reducedMotion.matches) return;
  const scope = animateCoordinate
    ? canvas.querySelector(`li.branch[data-coordinate="${CSS.escape(animateCoordinate)}"]`) ?? canvas
    : canvas;
  const cards = scope.querySelectorAll(".node-card");
  if (!cards.length) return;
  gsap.fromTo(cards, { y: -14, opacity: 0, scale: .96 },
    { y: 0, opacity: 1, scale: 1, duration: .35, stagger: .03, ease: "expo.out", clearProps: "transform,opacity" });
}

export function applyZoom(canvas: HTMLElement | Document, zoom: number, animate = true) {
  const inner = canvas.querySelector<HTMLElement>(".canvas-inner");
  const label = document.querySelector<HTMLElement>("#zoom-level");
  if (label) label.textContent = `${Math.round(zoom * 100)}%`;
  if (!inner) return;
  if (animate && !reducedMotion.matches) gsap.to(inner, { scale: zoom, duration: .25, ease: "expo.out" });
  else gsap.set(inner, { scale: zoom });
}

/** Spring the collapse toggle that just collapsed, as a small "it landed" cue. */
export function pulseToggle(canvas: HTMLElement, coordinate: string) {
  if (reducedMotion.matches) return;
  const toggle = canvas.querySelector(`li.branch[data-coordinate="${CSS.escape(coordinate)}"] .toggle`);
  if (toggle) gsap.fromTo(toggle, { scale: .7 }, { scale: 1, duration: .3, ease: "back.out(3)" });
}
